import { EventBus } from '@/mixins';

const isFolder = item => item.wtype && item.wtype === 'folder';

const getContentType = (wtype, contentTypes) => {
  for (const [, type] of Object.entries(contentTypes)) {
    if (type.configs && type.configs.wtype === wtype) {
      return type;
    }
  }
  return null;
};

const getContentTypeByConfigArg = (arg, value, contentTypes) => {
  for (const [, type] of Object.entries(contentTypes)) {
    if (type.configs && type.configs[arg] === value) {
      return type;
    }
  }
  return null;
};

// Gets title field of a content type
const getTitleField = (contentType, defaultTitle) => {
  const { fields } = contentType;
  for (const key of Object.keys(fields)) {
    if (fields[key].isTitle) {
      return key;
    }
  }
  return defaultTitle;
};

const getWorkflowStates = (content, contentTypes, workflows) => { // Get the possible workflow states
  const wtype = content.parent_uuid ? content.wtype.replace('_variation', '') : content.wtype;
  const contentType = getContentType(wtype, contentTypes);

  const { workflow } = contentType.configs;
  return workflow ? workflows[workflow].states : [];
};

const editContent = (content, router, newTab = false) => {
  let parent = null;

  // If the content was solved in a relationship the parent has the following structure
  // exp.: { uuid: "center_variation_uuid", wtype: "center_variation", parent: { uuid: "center_123", wtype: "center"}}
  if (content.parent) {
    // eslint-disable-next-line prefer-destructuring
    parent = content.parent;
  }

  //
  if (content.parent_uuid) {
    parent = {
      uuid: content.parent_uuid,
      wtype: content.parent_wtype
    };
  }

  const uuid = parent ? parent.uuid : content.uuid;
  let wtype = parent ? parent.wtype : content.wtype;

  if (parent && parent.uuid && !parent.wtype) { // Fix if we have parent uuid without parent wtype
    wtype = content.wtype.replace('_variation', '');
  }

  // If founded content type goes to a specific route
  if (wtype) {
    const path = { name: 'ContentEditor', params: { wtype: wtype, uuid: uuid } };

    if (parent && parent.uuid) { // ContentEditor with variations
      path.query = { variation: content.uuid };
    }

    // It opens in new tab
    if (newTab) {
      const routeResolved = router.resolve(path);
      window.open(routeResolved.href, '_blank');
    } else {
      router.push(path);
    }
  }
};

const getBreadcrumbs = fullPath => {
  const breadcrumbs = [{ name: 'Home', path: '/' }];

  // Auxiliar variables
  let path = '/';

  // Creates array with all "folders path"
  const compensated = fullPath.split('/');

  // Generates a list of breadcrumbs
  compensated.forEach((value, i) => {
    if (value !== '') { // If element from the split is not empty add it to the breadcrumb
      path += value;
      breadcrumbs.push({ name: value, path: path });
      path += '/';
    }
  });

  return breadcrumbs;
};

const getFacets = contentType => {
  const facets = [];
  const fields = contentType ? contentType.fields : {};

  // Generates the header for the v-data-table component with the content type specifications
  for (const key of Object.keys(fields)) {
    // If the field is supposed to be shown in the table header
    if (fields[key].facetType) {
      const newRefinement = {
        key: key,
        attribute: (fields[key].facetSettings && fields[key].facetSettings.subField) ? `${key}.${fields[key].facetSettings.subField}` : key,
        label: fields[key].label,
        order: fields[key].facetOrder,
        facetType: fields[key].facetType,
        facetSettings: fields[key].facetSettings,
        min: fields[key].facetMin ? fields[key].facetMin : null,
        max: fields[key].facetMax ? fields[key].facetMax : null
      };
      facets.push(newRefinement);
    }
  }

  facets.sort((a, b) => a.order - b.order);

  return facets;
};

const getCustomHeaders = contentType => {
  const headers = [];
  const fields = contentType ? contentType.fields : {};

  // Generates the header for the v-data-table component with the content type specifications
  for (const key of Object.keys(fields)) {
    // If the field is supposed to be shown in the table header
    if (fields[key].showInList) {
      const newHeader = {
        text: fields[key].label,
        order: fields[key].orderInList,
        attribute: key,
        isTitle: !!fields[key].isTitle
      };

      if (Array.isArray(fields[key].showInList) && fields[key].showInList.length && (['Picker', 'Entity'].includes(fields[key].viewType))) {
        newHeader.subAttributes = fields[key].showInList.map(property => `${key}.${property}`);
      }

      if (fields[key].sortBy) {
        // we only need .value when sortable
        newHeader.value = fields[key].sortBy;
      } else {
        newHeader.sortable = false;
      }

      // to format date
      if (fields[key].type === 'Date') {
        newHeader.isDate = true;
      }

      if (fields[key].options) {
        const optionsMap = {};
        for (const option of fields[key].options) {
          optionsMap[option.value] = option.text;
        }
        newHeader.optionsMap = optionsMap;
      }

      headers.push(newHeader);
    }
  }
  headers.sort((a, b) => a.order - b.order);
  headers.sort((a, b) => b.isTitle - a.isTitle);

  return headers;
};

const difference = (object, base) => {
  function changes (object, base) {
    let arrayIndexCounter = 0;
    return _.transform(object, (result, value, key) => {
      // check the differences between the new and old object
      if (!_.isEqual(value, base[key])) {
        // object tha has change
        const resultKey = _.isArray(base) ? arrayIndexCounter++ : key;
        // if the current values are a object check again, if not gives the value changed
        result[resultKey] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value;
      }
    });
  }
  return changes(object, base);
};

const setDefaultValues = (data, contentType) => {
  for (const key of Object.keys(contentType.fields)) {
    const field = { ...contentType.fields[key] };

    if (field.hasOwnProperty('defaultValue') && (!data.hasOwnProperty(key) || data[key] === null)) {
      // Use Vue.set to keep reactivity
      data[key] = field.defaultValue;
    }
  }

  return data;
};

const showInListFields = fields => {
  const listFields = {};

  _.forEach(fields, (field, key) => {
    if (field.showInList) {
      listFields[key] = field;
    }
  });

  return listFields;
};

const getValidContentTypes = (rawContentTypes, workspace, locale) => Object.keys(rawContentTypes).map(key => {
  // Exclude
  if (rawContentTypes[key].showInWorkspaces && !rawContentTypes[key].showInWorkspaces.includes('*')) {
    if (!rawContentTypes[key].showInWorkspaces.includes(workspace.code)) {
      return;
    }
  }

  if (rawContentTypes[key].hideInCTList) {
    return;
  }

  if (!(rawContentTypes[key].configs && rawContentTypes[key].configs.wtype)) {
    return;
  }

  const wtype = rawContentTypes[key].configs.wtype.toLowerCase();

  if (workspace.settings.hideInCTList && workspace.settings.hideInCTList.includes(wtype)) {
    return;
  }

  if (workspace.settings.creatableCTList && !workspace.settings.creatableCTList.includes(wtype)) {
    return;
  }

  // Can only create Content Types when the language is supported
  if (workspace.settings?.languages && !workspace.settings.languages.translatableContentTypes?.includes('*')) {
    if (locale && locale !== workspace.settings.languages.default && !workspace.settings.languages.translatableContentTypes.includes(wtype)) {
      return;
    }
  }

  // eslint-disable-next-line consistent-return
  return rawContentTypes[key];
});

const getMediaUrl = (path, transform) => {
  let url = 'https://media.ucpa.com/';

  if (path) {
    if (transform) {
      let transformation = transform;
      if (path.endsWith('.mp4')) {
        transformation = `video/upload/${transformation},so_0`;
        path = path.replace('.mp4', '.jpg');
      }
      path = transformation + path;
    }
    url += path;
  }

  return url;
};

export {
  difference,
  isFolder,
  getContentType,
  getContentTypeByConfigArg,
  getTitleField,
  getWorkflowStates,
  getCustomHeaders,
  editContent,
  getBreadcrumbs,
  getFacets,
  setDefaultValues,
  showInListFields,
  getValidContentTypes,
  getMediaUrl
};
